/* OP-CUSTOM: Draft line. Product selection is separate from the cart mutation. */
(() => {
  const experiment = window.OPCartOfferExperiment;
  if (!experiment?.enabled || customElements.get("op-cart-offer")) return;
  const strings = experiment.config.strings;
  const cache = new Map();
  const visits = new Set();
  let choices = new Map();
  let sourceKey = null;
  let active = false;
  let pendingUntil = 0;
  let pendingTimer;
  const notify = () => document.dispatchEvent(new CustomEvent("op:cart-busy"));
  window.OPCartOfferActions = {
    busy: () => active || Date.now() < pendingUntil,
    pending() {
      pendingUntil =
        Date.now() + (window.ON_CHANGE_DEBOUNCE_TIMER || 300) + 150;
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(notify, pendingUntil - Date.now() + 5);
      notify();
    },
    begin(kind) {
      if (active || (kind !== "quantity" && Date.now() < pendingUntil))
        return null;
      pendingUntil = 0;
      active = true;
      notify();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        active = false;
        notify();
      };
    },
  };
  const actions = window.OPCartOfferActions;
  const root = () => window.Shopify?.routes?.root || "/";
  const escape = (text) =>
    String(text ?? "").replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c]
    );
  const cleanName = (html) => {
    const node = document.createElement("textarea");
    node.innerHTML = html;
    return node.value;
  };
  const tee = (type) => String(type).toLowerCase() === "t-shirt";
  const request = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      signal: options.signal || AbortSignal.timeout(15000),
    });
    if (!response.ok) {
      const error = new Error("request");
      error.status = response.status;
      throw error;
    }
    return response;
  };
  const cart = async () =>
    (await request(`${root()}cart.js`, { cache: "no-store" })).json();
  async function recommendations(id, intent) {
    const key = `${id}:${intent}`;
    if (!cache.has(key)) {
      const url = `${root()}recommendations/products?section_id=op-cart-offer-recommendations&product_id=${id}&limit=10&intent=${intent}`;
      const pending = request(url)
        .then((r) => r.text())
        .then((html) => {
          const node = new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector("[data-op-recommendations]");
          if (!node) throw new Error("recommendations");
          return JSON.parse(node.textContent).products;
        })
        .catch((error) => {
          cache.delete(key);
          throw error;
        });
      cache.set(key, pending);
    }
    return cache.get(key);
  }
  class OPCartOffer extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.config = JSON.parse(
        this.querySelector("[data-op-offer-config]").textContent
      );
      this.drawer = this.closest("cart-drawer");
      if (!this.drawer) return;
      this.products = [];
      this.selected = null;
      this.loaded = false;
      this.loading = false;
      this.originalShipping = {
        label: this.drawer.querySelector(".totals__shipping")?.textContent,
        message: this.drawer.querySelector(".op-cart-shipping__message")
          ?.textContent,
      };
      this.drawer.classList.remove("op-offer-active");
      const signal = this.abort.signal;
      this.addEventListener("click", this.click.bind(this), { signal });
      this.addEventListener("keydown", this.keydown.bind(this), { signal });
      this.addEventListener(
        "keyup",
        (event) => {
          if (this.swallowedEscape && event.key === "Escape") {
            event.stopPropagation();
            this.swallowedEscape = false;
          }
        },
        { signal }
      );
      document.addEventListener(
        "pointerdown",
        (event) => {
          if (this.panelOpen && !this.contains(event.target))
            this.closePanel(false);
        },
        { signal }
      );
      document.addEventListener(
        "op:offer-permission",
        () => {
          this.loaded = false;
          this.loading = false;
          this.activate();
        },
        { signal }
      );
      document.addEventListener("op:cart-busy", () => this.syncBusy(), {
        signal,
      });
      document.addEventListener("visibilitychange", () => this.trackView(), {
        signal,
      });
      window.addEventListener("resize", () => this.positionPanel(), { signal });
      this.drawer.querySelector(".op-cart-middle")?.addEventListener(
        "scroll",
        () => {
          this.positionPanel();
          this.trackView();
        },
        { signal }
      );
      this.observer = new MutationObserver(() => {
        if (this.drawer.classList.contains("active")) this.activate();
        else this.closePanel(false);
      });
      this.observer.observe(this.drawer, {
        attributes: true,
        attributeFilter: ["class"],
      });
      this.querySelector("[data-op-rail]").addEventListener(
        "scroll",
        () => {
          this.arrows();
          this.trackView();
        },
        { signal }
      );
      this.activate();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.visibility?.disconnect();
      this.closePanel(false);
    }
    eligible() {
      const c = this.config;
      return (
        c.count === 1 &&
        c.showShipping &&
        c.shipping.state === "paid" &&
        c.source.requiresShipping &&
        tee(c.source.type) &&
        c.source.size &&
        c.source.color
      );
    }
    async activate() {
      if (
        !this.drawer.classList.contains("active") ||
        this.loading ||
        this.loaded
      )
        return;
      this.loading = true;
      try {
        await experiment.ready();
        if (!this.isConnected || !experiment.permitted()) {
          this.hide();
          return;
        }
        if (!this.eligible()) {
          if (this.config.count !== 1) {
            sourceKey = null;
            choices = new Map();
          }
          if (
            experiment.group() === "offer" &&
            this.config.count > 1 &&
            this.config.shipping.state === "free"
          )
            this.freeCopy();
          this.loaded = true;
          return;
        }
        const group = await experiment.enroll(this.config.source.id);
        if (!this.isConnected || group !== "offer") {
          this.loaded = true;
          return;
        }
        const key = `${this.config.source.key}:${this.config.source.size}:${this.config.source.color}`;
        if (key !== sourceKey) {
          choices = new Map();
          visits.clear();
          sourceKey = key;
        }
        let products = this.filter(
          await recommendations(this.config.source.id, "complementary")
        );
        if (!products.length)
          products = this.filter(
            await recommendations(this.config.source.id, "related")
          );
        if (!this.isConnected || !experiment.permitted()) return;
        this.products = products.slice(0, 5);
        this.loaded = true;
        if (!this.products.length) return;
        this.renderCards();
        this.hidden = false;
        this.drawer.classList.add("op-offer-active", "op-offer-layout");
        const shippingLabel = this.drawer.querySelector(".totals__shipping");
        if (shippingLabel) shippingLabel.textContent = strings.shipping;
        this.arrows();
        this.visibility = new IntersectionObserver(() => this.trackView(), {
          root: this.drawer.querySelector(".op-cart-middle"),
          threshold: [0, 0.5, 1],
        });
        this.visibility.observe(this.querySelector(".op-offer__heading"));
        this.querySelectorAll("[data-op-product]").forEach((card) =>
          this.visibility.observe(card)
        );
      } catch (_) {
        this.hide();
      } finally {
        this.loading = false;
      }
    }
    hide() {
      this.loaded = true;
      this.hidden = true;
      this.drawer?.classList.remove("op-offer-active", "op-offer-layout");
      const label = this.drawer?.querySelector(".totals__shipping");
      const message = this.drawer?.querySelector(".op-cart-shipping__message");
      if (label && this.originalShipping?.label)
        label.textContent = this.originalShipping.label;
      if (message && this.originalShipping?.message)
        message.textContent = this.originalShipping.message;
      this.closePanel(false);
    }
    filter(products) {
      const seen = new Set([
        Number(this.config.source.id),
        ...(this.config.source.linkedIds || []),
      ]);
      return products
        .map((p) => ({ ...p }))
        .filter((p) => {
          if (
            seen.has(p.id) ||
            (p.linkedIds || []).some((id) => seen.has(id)) ||
            !tee(p.type) ||
            !p.available ||
            p.requiresSellingPlan ||
            p.variantCount > 100 ||
            p.variantCount !== p.variants.length ||
            p.options.length !== 2
          )
            return false;
          p.sizeIndex = p.options.findIndex(
            (o) => o.name.toLowerCase() === "size"
          );
          p.colorIndex = p.options.findIndex((o) =>
            ["color", "colour"].includes(o.name.toLowerCase())
          );
          if (p.sizeIndex < 0 || p.colorIndex < 0) return false;
          p.name = cleanName(p.name);
          p.variants = p.variants.map((v) => ({
            ...v,
            available:
              v.available &&
              v.requiresShipping &&
              v.weight + this.config.weight >= this.config.shipping.threshold,
          }));
          if (!p.variants.some((v) => v.available)) return false;
          seen.add(p.id);
          (p.linkedIds || []).forEach((id) => seen.add(id));
          return true;
        });
    }
    choice(p) {
      if (!choices.has(p.id)) {
        const colors = p.options[p.colorIndex].values.map((v) => v.name);
        const ordered = [
          ...new Set([this.config.source.color, ...colors]),
        ].filter((c) => colors.includes(c));
        const color =
          ordered.find((c) =>
            p.variants.some(
              (v) =>
                v.available &&
                v.options[p.colorIndex] === c &&
                v.options[p.sizeIndex] === this.config.source.size
            )
          ) || ordered[0];
        choices.set(p.id, { color, size: this.config.source.size });
      }
      return choices.get(p.id);
    }
    variant(p) {
      const c = this.choice(p);
      return p.variants.find(
        (v) =>
          v.options[p.colorIndex] === c.color &&
          v.options[p.sizeIndex] === c.size
      );
    }
    price(p) {
      return (
        this.variant(p)?.money ||
        `${strings.price_from} ${p.variants.reduce((a, b) => (a.price < b.price ? a : b)).money}`
      );
    }
    image(p) {
      const c = this.choice(p);
      return (
        this.variant(p)?.image ||
        p.variants.find((v) => v.options[p.colorIndex] === c.color)?.image ||
        p.image
      );
    }
    swatch(p, color) {
      const native = p.options[p.colorIndex].values.find(
        (v) => v.name === color
      )?.swatch;
      const custom = String(experiment.config.swatches || "")
        .split("\n")
        .map((v) => v.split(":"))
        .find((v) => v[0].trim().toLowerCase() === color.toLowerCase())?.[1]
        ?.trim();
      const value = native || custom || color;
      return CSS.supports("color", value) ? value : "transparent";
    }
    renderCards() {
      const rail = this.querySelector("[data-op-rail]");
      rail.innerHTML = this.products
        .map((p) => {
          const c = this.choice(p),
            selected = this.selected === p.id;
          const sold = !this.variant(p)?.available;
          return `<button type="button" class="op-offer__card" data-op-product="${p.id}" aria-pressed="${selected}" aria-label="${escape(`${strings.select_design} ${p.title}, ${c.color}, ${c.size}, ${this.price(p)}`)}"><span class="op-offer__image"><img src="${escape(this.image(p))}" alt="" width="130" height="130"><span class="op-offer__selected" ${selected ? "" : "hidden"}>${escape(strings.selected)}</span></span><span class="op-offer__name">${escape(p.name)}</span><span class="op-offer__color">${escape(c.color)}</span><span class="op-offer__price">${escape(this.price(p))}</span>${sold ? `<span class="op-offer__sold">${escape(c.size)} ${escape(strings.sold_out)}</span>` : ""}</button>`;
        })
        .join("");
      this.syncBusy();
    }
    renderSelection() {
      const p = this.products.find((p) => p.id === this.selected);
      if (!p) return;
      const c = this.choice(p),
        v = this.variant(p);
      this.querySelector("[data-op-hint]").hidden = true;
      this.querySelector("[data-op-selection]").hidden = false;
      this.querySelector("[data-op-summary]").textContent =
        `${p.name} · ${c.color} · ${c.size}`;
      this.querySelector("[data-op-price]").textContent = this.price(p);
      this.querySelector("[data-op-size]").textContent = c.size;
      this.querySelector("[data-op-swatch]").style.backgroundColor =
        this.swatch(p, c.color);
      const color = this.querySelector('[data-op-action="color"]');
      color.setAttribute("aria-label", `${strings.change_color}: ${c.color}`);
      color.dataset.single = String(
        p.options[p.colorIndex].values.length === 1
      );
      this.querySelector('[data-op-action="size"]').setAttribute(
        "aria-label",
        `${strings.change_size}: ${c.size}`
      );
      this.querySelector("[data-op-note]").textContent = v?.available
        ? ""
        : strings.unavailable
            .replace("[size]", c.size)
            .replace("[color]", c.color);
      this.querySelector("[data-op-colors]").innerHTML = p.options[
        p.colorIndex
      ].values
        .map(
          ({ name }) =>
            `<button type="button" data-op-color="${escape(name)}" aria-pressed="${name === c.color}"><span class="op-offer__swatch" style="background-color:${escape(this.swatch(p, name))}"></span>${escape(name)}</button>`
        )
        .join("");
      this.querySelector("[data-op-sizes]").innerHTML = p.options[
        p.sizeIndex
      ].values
        .map(({ name }) => {
          const available = p.variants.some(
            (v) =>
              v.available &&
              v.options[p.colorIndex] === c.color &&
              v.options[p.sizeIndex] === name
          );
          return `<button type="button" data-op-size-choice="${escape(name)}" aria-pressed="${name === c.size}" ${available ? "" : "disabled"}>${escape(name)}${available ? "" : `<span class="visually-hidden"> ${escape(strings.sold_out)}</span>`}</button>`;
        })
        .join("");
      this.syncBusy();
      if (this.panelOpen) this.positionPanel();
    }
    syncBusy() {
      const busy = actions.busy();
      this.querySelectorAll("button").forEach((button) => {
        if (button.hasAttribute("data-op-size-choice")) return;
        button.disabled = busy;
      });
      const p = this.products.find((p) => p.id === this.selected);
      const add = this.querySelector('[data-op-action="add"]');
      if (add) {
        add.disabled =
          busy || (!this.uncertain && (!p || !this.variant(p)?.available));
        add.textContent = this.adding
          ? strings.adding
          : this.uncertain
            ? strings.check_cart
            : this.error
              ? strings.retry
              : strings.add;
        add.setAttribute("aria-busy", String(Boolean(this.adding)));
      }
      const color = this.querySelector('[data-op-action="color"]');
      if (color?.dataset.single === "true") color.disabled = true;
      if (busy && !this.adding) this.closePanel(false);
    }
    click(event) {
      const button = event.target.closest("button");
      if (!button || button.disabled || actions.busy()) return;
      if (button.dataset.opProduct) {
        this.closePanel(false);
        const id = Number(button.dataset.opProduct),
          changed = this.selected !== id;
        this.selected = id;
        this.renderCards();
        this.renderSelection();
        this.querySelector(`[data-op-product="${id}"]`)?.focus({
          preventScroll: true,
        });
        this.querySelector("[data-op-selection]").scrollIntoView({
          block: "nearest",
          behavior: "instant",
        });
        if (changed)
          experiment.emit("op_cart_offer_select", {
            source_product_id: String(this.config.source.id),
            product_id: String(id),
          });
      } else if (button.dataset.opColor || button.dataset.opSizeChoice) {
        const p = this.products.find((p) => p.id === this.selected),
          c = this.choice(p);
        const attribute = button.dataset.opColor
          ? "data-op-color"
          : "data-op-size-choice";
        const value = button.getAttribute(attribute);
        if (attribute === "data-op-color") c.color = value;
        else c.size = value;
        this.renderCards();
        this.renderSelection();
        [...this.querySelectorAll(`[${attribute}]`)]
          .find((n) => n.getAttribute(attribute) === value)
          ?.focus({ preventScroll: true });
      } else {
        const action = button.dataset.opAction;
        if (action === "color" || action === "size") this.togglePanel(button);
        if (action === "add") {
          this.closePanel(false);
          this.add();
        }
        if (action === "previous" || action === "next")
          this.querySelector("[data-op-rail]").scrollBy({
            left: (action === "next" ? 1 : -1) * 220,
            behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
              ? "instant"
              : "smooth",
          });
      }
    }
    keydown(event) {
      if (event.key === "Escape" && this.panelOpen) {
        event.preventDefault();
        event.stopPropagation();
        this.swallowedEscape = true;
        this.closePanel(true);
      }
      const card = event.target.closest("[data-op-product]");
      if (card && ["ArrowLeft", "ArrowRight"].includes(event.key)) {
        event.preventDefault();
        const cards = [...this.querySelectorAll("[data-op-product]")];
        const next =
          cards[
            (cards.indexOf(card) +
              (event.key === "ArrowRight" ? 1 : cards.length - 1)) %
              cards.length
          ];
        next.focus();
        next.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
    }
    togglePanel(button) {
      if (this.panelOpen) return this.closePanel(true);
      this.panelTrigger = button;
      this.panelOpen = true;
      const panel = this.querySelector(".op-offer__options");
      panel.hidden = false;
      if (panel.showPopover) {
        panel.setAttribute("popover", "manual");
        panel.showPopover();
      }
      this.querySelectorAll('[aria-controls="OpCartOfferOptions"]').forEach(
        (n) => n.setAttribute("aria-expanded", "true")
      );
      this.positionPanel();
      panel
        .querySelector(
          button.dataset.opAction === "size"
            ? "[data-op-sizes] button:not([disabled])"
            : "[data-op-colors] button"
        )
        ?.focus({ preventScroll: true });
    }
    closePanel(focus) {
      if (!this.panelOpen) return;
      this.panelOpen = false;
      const panel = this.querySelector(".op-offer__options");
      if (panel?.matches(":popover-open")) panel.hidePopover();
      if (panel) panel.hidden = true;
      this.querySelectorAll('[aria-controls="OpCartOfferOptions"]').forEach(
        (n) => n.setAttribute("aria-expanded", "false")
      );
      if (focus && this.panelTrigger?.isConnected)
        this.panelTrigger.focus({ preventScroll: true });
    }
    positionPanel() {
      if (!this.panelOpen) return;
      const panel = this.querySelector(".op-offer__options"),
        row = this.querySelector(".op-offer__controls").getBoundingClientRect();
      const box = this.drawer
        .querySelector(".drawer__inner")
        .getBoundingClientRect();
      panel.style.width = `${Math.min(340, box.width - 32)}px`;
      const above = Math.max(0, row.top - 20);
      const below = Math.max(0, innerHeight - row.bottom - 20);
      const useAbove = above >= below;
      // Keep the shared panel off its controls, even on a short mobile viewport.
      panel.style.maxHeight = `${Math.max(80, useAbove ? above : below)}px`;
      const height = panel.getBoundingClientRect().height;
      panel.style.left = `${Math.max(box.left + 16, Math.min(row.left, box.right - panel.offsetWidth - 16))}px`;
      panel.style.top = `${Math.max(12, useAbove ? row.top - height - 8 : row.bottom + 8)}px`;
    }
    arrows() {
      const rail = this.querySelector("[data-op-rail]");
      this.querySelector('[data-op-action="previous"]').hidden =
        rail.scrollLeft < 5;
      this.querySelector('[data-op-action="next"]').hidden =
        rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
    }
    trackView() {
      if (
        this.hidden ||
        document.visibilityState !== "visible" ||
        !this.products.length ||
        !this.drawer.classList.contains("active") ||
        visits.has(sourceKey)
      )
        return;
      const body = this.drawer
        .querySelector(".op-cart-middle")
        .getBoundingClientRect();
      const visible = (node) => {
        const r = node.getBoundingClientRect();
        return (
          r.top >= body.top &&
          r.bottom <= body.bottom &&
          r.right > body.left &&
          r.left < body.right
        );
      };
      if (
        visible(this.querySelector(".op-offer__heading")) &&
        [...this.querySelectorAll("[data-op-product]")].some(visible)
      ) {
        const sent = experiment.emit("op_cart_offer_view", {
          source_product_id: String(this.config.source.id),
        });
        if (sent.ga || sent.clarity) visits.add(sourceKey);
      }
    }
    freeCopy() {
      this.drawer.classList.add("op-offer-layout");
      const node = this.drawer.querySelector(".op-cart-shipping__message");
      if (node) node.textContent = strings.free;
      const label = this.drawer.querySelector(".totals__shipping");
      if (label) label.textContent = strings.shipping;
    }
    async refresh(state, sections, focusSelector) {
      const required = this.drawer.getSectionsToRender();
      const valid = (values) =>
        required.every(
          (s) =>
            typeof values?.[s.id] === "string" &&
            new DOMParser()
              .parseFromString(values[s.id], "text/html")
              .querySelector(s.selector || ".shopify-section")
        );
      if (!valid(sections)) {
        const url = new URL(location.href);
        url.searchParams.set("sections", required.map((s) => s.id).join(","));
        sections = await (await request(url)).json();
      }
      if (!valid(sections)) throw new Error("sections");
      this.drawer.classList.toggle("is-empty", state.item_count === 0);
      this.drawer.renderContents({ ...state, sections }, { focusSelector });
      document.dispatchEvent(
        new CustomEvent("cart:change", {
          detail: { cart: state, sectionId: "op-cart-offer" },
        })
      );
      window.publish?.(PUB_SUB_EVENTS.cartUpdate, { source: "op-cart-offer" });
    }
    async add() {
      const release = actions.begin("offer");
      if (!release) return;
      this.adding = true;
      this.syncBusy();
      const drawer = this.drawer;
      drawer.classList.add("op-cart-mutating");
      const controls = [
        ...drawer.querySelectorAll(
          "cart-drawer-items button,cart-drawer-items input,#CartDrawer-Checkout"
        ),
      ].map((n) => [n, n.disabled]);
      controls.forEach(([n]) => {
        n.disabled = true;
      });
      let committed = false;
      const p = this.products.find((p) => p.id === this.selected),
        v = p && this.variant(p);
      try {
        if (this.uncertain) {
          const state = await cart();
          await this.refresh(state, null, "#CartDrawer-Checkout");
          return;
        }
        if (!v?.available) return;
        const before = await cart();
        if (
          before.item_count !== 1 ||
          before.items[0]?.key !== this.config.source.key
        ) {
          await this.refresh(before, null);
          return;
        }
        let response;
        try {
          response = await (
            await request(`${root()}cart/add.js`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                items: [{ id: v.id, quantity: 1 }],
                sections: drawer.getSectionsToRender().map((s) => s.id),
                sections_url: location.pathname,
              }),
            })
          ).json();
          committed = true;
        } catch (error) {
          if (error.status && error.status < 500) throw error;
          const current = await cart().catch(() => null);
          if (
            current?.item_count === 2 &&
            current.items.some(
              (i) => i.variant_id === v.id && i.quantity === 1
            ) &&
            current.items.some(
              (i) => i.key === this.config.source.key && i.quantity === 1
            )
          ) {
            committed = true;
            response = {};
          } else {
            this.uncertain = true;
            throw error;
          }
        }
        document.dispatchEvent(
          new CustomEvent("variant:add", {
            detail: {
              variant: { id: String(v.id) },
              quantity: 1,
              formElement: null,
              sectionId: "op-cart-offer",
            },
          })
        );
        experiment.emit("op_cart_offer_add", {
          source_product_id: String(this.config.source.id),
          product_id: String(p.id),
          variant_id: String(v.id),
        });
        const state = await cart();
        await this.refresh(state, response.sections, "#CartDrawer-Checkout");
      } catch (error) {
        this.uncertain ||= committed;
        this.error = true;
        const message = this.uncertain ? strings.uncertain : strings.error;
        const node = this.querySelector("[data-op-error]");
        if (node) {
          node.textContent = message;
          node.hidden = false;
        }
        experiment.emit("op_cart_offer_error", {
          reason: this.uncertain
            ? "reconcile"
            : error.status === 422
              ? "unavailable"
              : "request",
        });
        document.dispatchEvent(
          new CustomEvent("cart:error", {
            detail: { source: "op-cart-offer", message },
          })
        );
      } finally {
        controls.forEach(([n, disabled]) => {
          if (n.isConnected) n.disabled = disabled;
        });
        drawer.classList.remove("op-cart-mutating");
        this.adding = false;
        release();
        if (this.isConnected) this.syncBusy();
      }
    }
  }
  customElements.define("op-cart-offer", OPCartOffer);
})();
